import React from "react";
import { observer, useLocalObservable } from "mobx-react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  useTheme,
  Theme,
  TableSortLabel,
  Box,
  Modal,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link, useNavigate } from "react-router-dom";
import {
  CustomMenu,
  CustomMenuItem,
  StyledTableCell,
  StyledTableRow,
} from "../../../styles/Shipmentstyles";
import { Category } from "../../../models/Categories/types";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { useStores } from "../../../use-stores";
import AppTableHeaderCell from "../../components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../components/common/pages/AppTablePagenation";
import AppButton from "../../components/common/buttons/AppButton";
import { SortType } from "../../../models/SortType";
import { formatDate } from "../../components/common/functions/formatDate";

export interface CategoryTableProps {
  /** Sticky header scroll area — number (px) or CSS length, e.g. `calc(100dvh - 260px)`. */
  maxHeight: number | string;
  categories: Category[];
  limit: number;
  page_no: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  onEditCategory?: (categoryId: number) => void;
}

const thumbImgSx = {
  maxHeight: "55px",
  minHeight: "55px",
  maxWidth: "55px",
  minWidth: "55px",
  objectFit: "cover" as const,
  borderRadius: "4px",
};

function CategoryNameThumb({
  imageUrl,
  name,
}: {
  imageUrl?: string | null;
  name: string;
}) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const trimmed = imageUrl?.trim();
  const showImg = Boolean(trimmed) && !imgFailed;

  return (
    <Box
      sx={{
        position: "relative",
        background: "rgba(242, 242, 242, 1)",
        padding: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginRight: 2,
        minHeight: "70px",
        maxHeight: "70px",
        minWidth: "70px",
        maxWidth: "70px",
        borderRadius: "4px",
        flexShrink: 0,
      }}
    >
      {showImg ? (
        <Box
          component="img"
          src={trimmed}
          alt=""
          sx={thumbImgSx}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <Typography
          variant="subtitle2"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            fontSize: "1.1rem",
          }}
        >
          {(name?.trim()?.charAt(0) || "?").toUpperCase()}
        </Typography>
      )}
    </Box>
  );
}

function CategoryTable({
  maxHeight,
  categories,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onEditCategory,
}: CategoryTableProps) {
  const navigate = useNavigate();
  const theme = useTheme<Theme>();
  const { userStore } = useStores();
  /** Require explicit `edit_category`; do not grant via `all` alone. */
  const showCategoryRowActions = userStore.hasPermission(
    EmployeeRoles.EDIT_CATEGORY,
    true,
  );
  const ui = useLocalObservable(() => ({
    anchorEl: null as HTMLElement | null,
    currentCategoryId: null as number | null,
    selectedSeoDescription: "",
    seoDescModalOpen: false,
  }));

  const isOpen = Boolean(ui.anchorEl);

  const renderSortIcon = (property: string) => {
    if (sortBy === property) return sortType === SortType.ASC ? "↑" : "↓";
    return "↑↓";
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    categoryId: number,
  ) => {
    ui.anchorEl = event.currentTarget;
    ui.currentCategoryId = categoryId;
  };

  const handleMenuClose = () => {
    ui.anchorEl = null;
    ui.currentCategoryId = null;
  };

  const handleEdit = () => {
    if (ui.currentCategoryId == null) return;
    if (onEditCategory) {
      onEditCategory(ui.currentCategoryId);
    } else {
      navigate(
        `/categories/add-edit-category?category-id=${ui.currentCategoryId}`,
      );
    }
    handleMenuClose();
  };

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  return (
    <>
      <Paper sx={{ marginTop: "0.6rem" }}>
        <TableContainer
          sx={{
            maxHeight,
            maxWidth: "100%",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell
                  sx={{
                    textAlign: "center",
                    fontSize: 14,
                    position: "sticky",
                    left: 0,
                    top: 0,
                    zIndex: (t) => t.zIndex.appBar + 2,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                  }}
                >
                  <TableSortLabel
                    active={sortBy === "id"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "id",
                        sortBy === "id" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC,
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon("id")}</span>}
                    sx={{ fontSize: "12px" }}
                  >
                    ID
                  </TableSortLabel>
                </StyledTableCell>
                <AppTableHeaderCell minWidth="250px">
                  <TableSortLabel
                    active={sortBy === "name"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "name",
                        sortBy === "name" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC,
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon("name")}</span>}
                    sx={{ fontSize: "12px" }}
                  >
                    Name
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "priority"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "priority",
                        sortBy === "priority" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC,
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("priority")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Priority
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "display"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "display",
                        sortBy === "display" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC,
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("display")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Display
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "active"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "active",
                        sortBy === "active" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC,
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("active")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Active
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  title="Created Date"
                  minWidth="120px"
                  fontSize="12px"
                />
                <AppTableHeaderCell
                  title="Modified Date"
                  minWidth="120px"
                  fontSize="12px"
                />
                <AppTableHeaderCell title="SEO Title" fontSize="12px" />
                <AppTableHeaderCell title="SEO Description" fontSize="12px" />
                {showCategoryRowActions ? (
                  <AppTableHeaderCell
                    title="Actions"
                    minWidth="100px"
                    fontSize="12px"
                    sx={{
                      color: theme.palette.customColors.tableGrayHeadingColor,
                      position: "sticky",
                      top: 0,
                      right: 0,
                      zIndex: (t) => t.zIndex.appBar + 2,
                      minWidth: "80px",
                      backgroundColor: theme.palette.background.paper,
                      boxShadow:
                        "-10px 0 18px -10px rgba(253, 250, 250, 0.35)",
                    }}
                  />
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((row) => (
                <StyledTableRow key={row.id}>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      textAlign: "center",
                      left: 0,
                      position: "sticky",
                      zIndex: 2,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor: theme.palette.background.paper,
                      borderBottomColor: "#E2E2E2",
                    }}
                  >
                    {row.id}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    <Box display="flex" alignItems="center">
                      <CategoryNameThumb
                        imageUrl={row.category_image_url}
                        name={row.name}
                      />
                      <Box sx={{ textAlign: "left", minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.name}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.priority ?? "—"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    <Chip
                      size="small"
                      label={Boolean(row.display) ? "True" : "False"}
                      color={row.display ? "success" : "default"}
                      variant={row.display ? "filled" : "outlined"}
                      sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    <Chip
                      size="small"
                      label={Boolean(row.active) ? "True" : "False"}
                      color={row.active ? "success" : "default"}
                      variant={row.active ? "filled" : "outlined"}
                      sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {formatDate(row.created_date)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {formatDate(row.modified_date)}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: "12px",
                      verticalAlign: "middle",
                      maxWidth: 220,
                    }}
                  >
                    {row.seo_title?.trim() ? (
                      <Typography
                        variant="body2"
                        noWrap
                        title={row.seo_title}
                      >
                        {row.seo_title.trim()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        --
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.seo_description?.trim() ? (
                      <AppButton
                        label="View"
                        variant="transparentPrimary"
                        customStyles={{
                          fontSize: "0.8rem",
                          padding: "4px 8px",
                          minWidth: "auto",
                        }}
                        onClick={() => {
                          ui.selectedSeoDescription =
                            row.seo_description || "";
                          ui.seoDescModalOpen = true;
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        --
                      </Typography>
                    )}
                  </TableCell>
                  {showCategoryRowActions ? (
                    <TableCell
                      sx={{
                        width: "10%",
                        fontSize: "12px",
                        color:
                          theme.palette.customColors.tableGrayHeadingColor,
                        position: "sticky",
                        zIndex: 10,
                        minWidth: "80px",
                        right: 0,
                        background: theme.palette.customColors.white,
                        boxShadow:
                          "-10px 0 18px -10px rgba(115,115,115,0.3)",
                        borderBottomColor: "#E2E2E2",
                      }}
                    >
                      <IconButton
                        onClick={(e) => handleMenuClick(e, row.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  ) : null}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {showCategoryRowActions ? (
          <CustomMenu
            anchorEl={ui.anchorEl}
            onClose={handleMenuClose}
            open={isOpen}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <CustomMenuItem onClick={() => handleEdit()}>
              <Link
                to={`/categories/add-edit-category?category-id=${ui.currentCategoryId ?? ""}`}
                style={{ textDecoration: "none", color: "black" }}
                onClick={(e) => {
                  if (onEditCategory) e.preventDefault();
                }}
              >
                View/Edit
              </Link>
            </CustomMenuItem>
          </CustomMenu>
        ) : null}
        <AppTablePagenation
          fontSize="12px"
          pageNo={page_no}
          limit={limit}
          totalCount={total_count}
          onPageChange={onPageChange}
          handleRowsChange={handleRowsChange}
        />
      </Paper>

      <Modal
        open={ui.seoDescModalOpen}
        onClose={() => {
          ui.seoDescModalOpen = false;
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "calc(100% - 24px)", sm: "90%" },
            maxWidth: 720,
            maxHeight: "min(85dvh, 85vh)",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 3 },
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              SEO Description
            </Typography>
            <IconButton
              onClick={() => {
                ui.seoDescModalOpen = false;
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
          >
            {ui.selectedSeoDescription || "No SEO description available."}
          </Typography>
        </Box>
      </Modal>
    </>
  );
}

export default observer(CategoryTable);
